import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { RulebookPage } from "@/components/RulebookPage";
import { Badge } from "@/components/ui/badge";

export function WealthLevel() {
  return (
    <RulebookPage title="Wealth & Currency">
      <h2 className="text-xl font-semibold mt-6 mb-2">Currency</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Across the multiverse, coins take many forms, but the metal itself is
        constant. In the world of Ultimate Warlock, the faces on the coins may
        depict kings from distant worlds, or come in unfamiliar shapes and
        sizes.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Much magic is fuelled by coin, expended as part of various rituals and
        used up in forging powerful items. If a spell or ability has a cost to
        use, it is listed in the ability&apos;s description.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        During the game, there will be a public bank of coins available for all
        players to use for exchange and payment, and freely give and take from.
        This works on an honesty system, so please respect it!
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Wealth Levels</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Your Wealth Level represents your social standing and the amount of coin
        you begin each game with.
      </p>

      <ScrollArea className="mb-4 w-full max-w-[90vw]">
        <table className="w-max max-w-[200vw] text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Wealth Level
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Title
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Starting Coin
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Armour Access
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-t border-border">0</td>
              <td className="px-3 py-2 border-t border-border">Beggar</td>
              <td className="px-3 py-2 border-t border-border">None</td>
              <td className="px-3 py-2 border-t border-border">None</td>
              <td className="px-3 py-2 border-t border-border">
                You have no personal possessions besides what you carry.
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">1</td>
              <td className="px-3 py-2 border-t border-border">Peasant</td>
              <td className="px-3 py-2 border-t border-border">2 silver</td>
              <td className="px-3 py-2 border-t border-border">Scrap Armour</td>
              <td className="px-3 py-2 border-t border-border">
                You may have a simple home which you share with others. You can
                only afford the necessities, and must work constantly to
                survive. You are one disaster away from ruin.
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">2</td>
              <td className="px-3 py-2 border-t border-border">
                Merchant/Artisan
              </td>
              <td className="px-3 py-2 border-t border-border">1 gold</td>
              <td className="px-3 py-2 border-t border-border">Light Armour</td>
              <td className="px-3 py-2 border-t border-border">
                You own a family home, and can afford basic comforts. You have a
                financial safety net in case of emergency, and have a set of
                skills you can put to use in any society to ensure a decent
                standard of living.
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">3</td>
              <td className="px-3 py-2 border-t border-border">Wealthy</td>
              <td className="px-3 py-2 border-t border-border">3 gold</td>
              <td className="px-3 py-2 border-t border-border">
                Medium Armour
              </td>
              <td className="px-3 py-2 border-t border-border">
                You pursue a lucrative career in banking, law, or business. You
                are often invited to exclusive events that those of a lower
                class could only dream of.
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">4</td>
              <td className="px-3 py-2 border-t border-border">Noble</td>
              <td className="px-3 py-2 border-t border-border">
                5 gold, plus 5 silver every dawn
              </td>
              <td className="px-3 py-2 border-t border-border">Heavy Armour</td>
              <td className="px-3 py-2 border-t border-border">
                You own the land that others live on. May work if you feel like
                it, typically as a politician. Your family trust and various
                investments afford you a daily income of an additional 5 silver
                every morning.
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">5</td>
              <td className="px-3 py-2 border-t border-border">Royalty</td>
              <td className="px-3 py-2 border-t border-border">
                5 gold, plus 5 silver every dawn
              </td>
              <td className="px-3 py-2 border-t border-border">Heavy Armour</td>
              <td className="px-3 py-2 border-t border-border">
                You have a prestigious title of your choice, and may insist on
                being referred to by it. Some say you are no better than regular
                nobles, but you know in your heart that being Royalty makes you
                different and special.
              </td>
            </tr>
          </tbody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <h2 className="text-xl font-semibold mt-6 mb-2">Starting Coin</h2>
      <p className="text-sm text-muted-foreground mb-3">
        You may collect your starting coin from the public bank before or during
        the game. Even if your Wealth Level rises during play, your starting
        coin does not change.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        You can withdraw this in denominations of your choice: so, for example,
        a Peasant may choose to take 10 copper and 1 silver, while a Wealthy
        Warlock may request 300 copper pieces (if they want to make the GMs
        cry).
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Between Games</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Any money you earn (or lose!) is reset between games: in the intervening
        time, it is assumed your character spends their newly acquired fortune,
        or slowly builds their savings back up after losing it all.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        However, treasures you craft, find, or buy stay with you, such as magic
        items, rare books, or special weapons.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Spending Coin</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Silver coins are the primary currency of the Challenge. Common uses
        include:
      </p>
      <ul className="list-disc list-inside text-sm text-muted-foreground mb-3 space-y-1">
        <li>Buying items from merchants and NPCs</li>
        <li>
          Paying for Short Rest recovery (1 silver to restore all HP and MP)
        </li>
        <li>Funding rituals and crafting</li>
        <li>Bribing NPCs</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">Earning Coin</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Characters can earn coin through:
      </p>
      <ul className="list-disc list-inside text-sm text-muted-foreground mb-3 space-y-1">
        <li>Completing quests and missions assigned by GMs</li>
        <li>Trading with other players (PvP trade is fully encouraged)</li>
        <li>Ill-Gotten Gains skill (taking from defeated NPCs)</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">Economic Notes</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Coin cannot be stolen from player characters — your purse is always safe
        from other players. However, NPCs can be robbed freely (subject to the
        Ill-Gotten Gains rules).
      </p>

      <Badge variant="outline">
        Carry your coin on your person — character sheets are not wallets
      </Badge>
    </RulebookPage>
  );
}
